import React, { useCallback, useEffect, useState } from 'react';
import {
  List,
  DateField,
  ArrayField,
  SingleFieldList,
  usePermissions,
  ReferenceArrayField,
  ReferenceArrayInput,
  AutocompleteArrayInput,
  useGetList,
  Identifier,
  useListFilterContext,
  useStore,
  ColumnsButton,
  FilterButton,
  TopToolbar,
  SelectField,
  SelectInput,
  DataTable,
  TextField,
} from 'react-admin';

import {
  Box,
  Card,
  CircularProgress,
  Divider,
  Typography,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { DateTime } from 'luxon';

import { capitalize } from 'lodash';
import ReferenceEntityField from '../../customFields/ReferenceEntityField';
import StatusField from '../../customFields/StatusField';
import ColoredChipField from '../../customFields/ColoredChipField';

import { SubListNavigation } from '../layout/SubNavigation';
import { StyledTab, StyledTabs } from '../layout/Tab';
import Pagination from '../layout/Pagination';
import NoResults from '../layout/NoResults';
import LinkField from '../layout/LinkField';

import { boldDataTableStyle } from '../../constants/style/dataTableStyles';

import hasAccess from '../../utilities/hasAccess';

import MergeCases from './bulkActions/MergeCases';
import BulkAssign from './bulkActions/BulkAssign';

import EntityFilter from '../filters/EntityFilter';
import RuleFilter from '../filters/RuleFilter';
import CaseAssigneeFilter from '../filters/CaseAssigneeFilter';
import actions, { Actions } from '../../constants/actions';
import caseStatuses, { CaseStatuses } from '../../constants/caseStatuses';
import CaseAssign from './components/CaseAssign';
import casePriority, { CasePriority } from '../../constants/casePriority';
import CaseEscalationFilter from '../filters/CaseEscalationFilter';
import CaseEscalate from './components/CaseEscalate';
import CaseProcessors from './components/CaseProcessors';
import EntityDisplayNameField from '../../customFields/EntityDisplayNameField';
import SearchField from '../../customFields/SearchField';
import SmartDateTimeInput from '../../customFields/SmartDateTimeInput';
import PermissionMissing from '../utils/PermissionMissing';
import dateParse, { dateFormat } from '../utils/dateParser';
import CaseDeadline from '../../customFields/CaseDeadline';
import CaseDescription, { CASE_DESCRIPTION_MAX_WIDTH } from './components/CaseDescription';

type CaseCount = {
  id: Identifier;
  open: number;
  closed: number;
  'in-progress': number;
  pending: number;
  scheduled: number;
};

const BulkActions = () => (
  <Box display="flex">
    <MergeCases />
    <BulkAssign />
  </Box>
);

export const CaseListBody = ({
  status,
  bulkActionButtons = true,
}: {
  status?: CaseStatuses;
  bulkActionButtons?: boolean;
}) => {
  const { permissions } = usePermissions<Actions[]>();
  const isClosedCase = status === caseStatuses.CLOSED;
  const isScheduledCase = status === caseStatuses.SCHEDULED;

  return (
    <DataTable
      bulkActionButtons={(hasAccess(permissions, 'CaseUpdate') && bulkActionButtons) ? <BulkActions /> : false}
      sx={boldDataTableStyle}
      empty={<NoResults variant="h6" />}
      hiddenColumns={['processors']}
    >
      <DataTable.Col label="ID" source="id" disableSort>
        {hasAccess(permissions, actions.CASE_GET) ? (
          <LinkField sortable={false} source="id" label="ID" />
        ) : (
          <TextField sortable={false} source="id" label="ID" />
        )}
      </DataTable.Col>
      <DataTable.Col source="createdAt">
        <DateField source="createdAt" showTime />
      </DataTable.Col>
      {hasAccess(permissions, actions.CASE_SUMMARY_CREATE) && (
        <DataTable.Col
          source="shortDescription"
          label={(
            <Box component="span" alignItems="center" display="inline-flex" gap={1}>
              <AutoAwesomeIcon aria-hidden="true" sx={{ color: 'text.secondary', fontSize: 14 }} />
              <span>Description</span>
            </Box>
          )}
          disableSort
          sx={{
            maxWidth: CASE_DESCRIPTION_MAX_WIDTH,
          }}
        >
          <CaseDescription />
        </DataTable.Col>
      )}
      {isClosedCase && (
        <DataTable.Col source="statusChangeDate" label="Closed at">
          <DateField source="closedAt" showTime emptyText="-" />
        </DataTable.Col>
      )}
      {isScheduledCase && (
        <DataTable.Col source="scheduledAt" label="Scheduled at">
          <DateField source="scheduledAt" emptyText="-" />
        </DataTable.Col>
      )}
      <DataTable.Col label="Entities" source="entities" disableSort>
        <ArrayField source="entities">
          <SingleFieldList linkType={false}>
            <ReferenceEntityField source="">
              <EntityDisplayNameField chip />
            </ReferenceEntityField>
          </SingleFieldList>
        </ArrayField>
      </DataTable.Col>
      <DataTable.Col label="Processor" source="processors" disableSort>
        {hasAccess(permissions, actions.REQUEST_LIST) ? <CaseProcessors /> : <PermissionMissing />}
      </DataTable.Col>
      <DataTable.Col source="tags">
        {hasAccess(permissions, actions.TAG_LIST) ? (
          <ReferenceArrayField source="tags" reference="tags" emptyText="-">
            <SingleFieldList placeholder="-">
              <ColoredChipField />
            </SingleFieldList>
          </ReferenceArrayField>
        ) : (<PermissionMissing />)}
      </DataTable.Col>
      <DataTable.Col label="Status" source="status" disableSort>
        <StatusField source="status" />
      </DataTable.Col>
      <DataTable.Col label="Assignee" source="assignee" disableSort>
        <CaseAssign label="Assignee" />
      </DataTable.Col>
      <DataTable.Col label="Escalated to" source="escalatedTo" disableSort>
        <CaseEscalate label="Escalated to" />
      </DataTable.Col>
      <DataTable.Col source="priority">
        <SelectField
          source="priority"
          choices={
            Object.values(casePriority)
              .map((priority) => ({
                name: capitalize(priority),
                id: priority,
              }))
          }
        />
      </DataTable.Col>
      {!isClosedCase && !isScheduledCase
      && (
        <DataTable.Col source="caseDeadline" label="Case deadline">
          <Box display="flex" alignItems="center" flexWrap="wrap" gap={2}>
            <CaseDeadline source="caseDeadline" emptyText="-" showTime />
          </Box>
        </DataTable.Col>
      )}
    </DataTable>
  );
};

const CaseCountLabel = ({
  count,
  label,
  countIsLoading,
  shouldCount,
}: {
  count?: number;
  label: string;
  countIsLoading: boolean;
  shouldCount: boolean;
}) => {
  if (!shouldCount) return <Typography>{label}</Typography>;

  if (countIsLoading) {
    return (
      <Box display="flex" alignItems="center" gap={2}>
        <Typography>{label}</Typography>
        <CircularProgress size={11} />
      </Box>
    );
  }

  return <Typography>{`${label} (${count ?? 0})`}</Typography>;
};

const CaseCountProvider = ({
  setCount,
  setCountIsLoading,
  shouldCount,
  setShouldCount,
  children,
}: {
  setCountIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  shouldCount: boolean;
  setShouldCount: React.Dispatch<React.SetStateAction<boolean>>;
  setCount: React.Dispatch<React.SetStateAction<CaseCount | undefined>>;
  children: React.ReactElement;
}) => {
  const { filterValues } = useListFilterContext();

  useEffect(
    () => {
      setShouldCount(Object.keys(filterValues).every((key) => key === 'status'));
    },
    [filterValues, setShouldCount],
  );

  const { data, isLoading } = useGetList<CaseCount>('cases/count', { filter: filterValues }, { enabled: shouldCount });

  useEffect(() => {
    setCountIsLoading(isLoading);
  }, [isLoading, setCountIsLoading]);

  useEffect(() => {
    setCount(data?.at(0));
  }, [data, setCount]);

  return children;
};

const ListActions = () => (
  <TopToolbar>
    <ColumnsButton />
    <FilterButton />
  </TopToolbar>
);

const isPastDate = (value: string) => {
  if (DateTime.fromISO(value) > DateTime.now()) return 'Invalid date';
  return undefined;
};

type CaseFilters = {
  search?: string;
  tags?: string[];
  entities?: string[];
  'pings.rule'?: string[];
  asignee?: string;
  escalatedTo?: string;
  priority?: CasePriority;
  openedFrom?: string;
  openedTo?: string;
}

const dateRangeValidation = (_value: string, allValues: CaseFilters) => {
  const { openedFrom, openedTo } = allValues;
  if (
    openedFrom
    && openedTo
  ) {
    if (DateTime.fromISO(openedFrom) > DateTime.fromISO(openedTo)) {
      return 'Invalid date range';
    }
    if (DateTime.fromISO(openedFrom).plus({ years: 5 }) < (DateTime.fromISO(openedTo))) {
      return 'Range greater than 5 years';
    }
  }
  return undefined;
};

const CaseList = () => {
  const [statusTab, setStatusTab] = useStore('case.statusTab', 0);
  const [status, setStatusFilter] = useStore<CaseStatuses>('case.status', caseStatuses.OPEN);

  const [countIsLoading, setCountIsLoading] = useState(false);
  const [shouldCount, setShouldCount] = useState(true);
  const [count, setCount] = useState<CaseCount>();

  const handleStatusChange = useCallback((
    _event: React.SyntheticEvent<Element, Event>,
    newValue: number,
  ) => {
    setStatusTab(newValue);
    if (newValue === 0) setStatusFilter(caseStatuses.OPEN);
    if (newValue === 1) setStatusFilter(caseStatuses.IN_PROGRESS);
    if (newValue === 2) setStatusFilter(caseStatuses.PENDING);
    if (newValue === 3) setStatusFilter(caseStatuses.CLOSED);
    if (newValue === 4) setStatusFilter(caseStatuses.SCHEDULED);
  }, [setStatusFilter, setStatusTab]);

  return (
    <List
      filter={{ status }}
      storeKey="case.list"
      empty={false}
      pagination={<Pagination disableEmptyText />}
      filters={[
        <SearchField source="search" label="Search" alwaysOn />,
        <ReferenceArrayInput perPage={100} source="tags" reference="tags" filter={{ active: true }}>
          <AutocompleteArrayInput
            label="Tag"
            filterToQuery={(searchText: string) => ({ name: searchText })}
            optionText="name"
          />
        </ReferenceArrayInput>,
        <EntityFilter label="Entities" source="entities" />,
        <RuleFilter source="pings.rule" label="Rules" />,
        <CaseAssigneeFilter source="assignee" />,
        <CaseEscalationFilter source="escalatedTo" />,
        <SelectInput
          fullWidth
          source="priority"
          label="Priority"
          choices={
            Object.values(casePriority)
              .map((item) => ({
                name: capitalize(item),
                id: item,
              }))
          }
        />,
        <SmartDateTimeInput
          label="Opened From Date"
          source="openedFrom"
          validate={[isPastDate, dateRangeValidation]}
          parse={dateParse}
          format={dateFormat}
          sx={{
            '& .MuiFormHelperText-root': {
              display: 'unset',
            },
          }}
        />,
        <SmartDateTimeInput
          label="Opened To Date"
          source="openedTo"
          validate={[isPastDate, dateRangeValidation]}
          parse={dateParse}
          format={dateFormat}
          sx={{
            '& .MuiFormHelperText-root': {
              display: 'unset',
            },
          }}
        />,
      ]}
      sort={{ field: 'createdAt', order: 'DESC' }}
      exporter={false}
      actions={<ListActions />}
      perPage={50}
    >
      <Card sx={{ margin: 0 }} variant="outlined">
        <CaseCountProvider
          shouldCount={shouldCount}
          setCount={setCount}
          setShouldCount={setShouldCount}
          setCountIsLoading={setCountIsLoading}
        >
          <Card sx={{ margin: 0 }}>
            <SubListNavigation>
              <StyledTabs value={statusTab} onChange={handleStatusChange}>
                <StyledTab
                  label={React.createElement(CaseCountLabel, {
                    count: count?.open,
                    label: 'Open',
                    countIsLoading,
                    shouldCount,
                  })}
                />
                <StyledTab
                  label={React.createElement(CaseCountLabel, {
                    count: count?.['in-progress'],
                    label: 'In progress',
                    countIsLoading,
                    shouldCount,
                  })}
                />
                <StyledTab
                  label={React.createElement(CaseCountLabel, {
                    count: count?.pending,
                    label: 'Pending',
                    countIsLoading,
                    shouldCount,
                  })}
                />
                <StyledTab
                  label={React.createElement(CaseCountLabel, {
                    count: count?.closed,
                    label: 'Closed',
                    countIsLoading,
                    shouldCount,
                  })}
                />
                <StyledTab
                  label={React.createElement(CaseCountLabel, {
                    count: count?.scheduled,
                    label: 'Scheduled',
                    countIsLoading,
                    shouldCount,
                  })}
                />
              </StyledTabs>
            </SubListNavigation>
            <Divider />
          </Card>
        </CaseCountProvider>
        <CaseListBody status={status} />
      </Card>
    </List>
  );
};

export default CaseList;
